import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Stack,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  InputAdornment,
  Alert,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from '@mui/material';
import {
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
  CalendarToday as CalendarTodayIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  TableChart as ExcelIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { supabase } from '../services/supabase';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

interface Employee {
  id: string;
  employee_id: string;
  first_name: string;
  last_name: string;
  department: string;
  position: string;
  hire_date: string;
  basic_salary: number;
  avatar_url?: string;
}

interface ThirteenthMonthData {
  employee: Employee;
  totalBasicSalary: number;
  monthsWorked: number;
  thirteenthMonthPay: number;
  eligibilityStatus: 'Eligible' | 'Not Eligible';
  serviceMonths: number;
}

interface Department {
  id: string;
  name: string;
}

const ThirteenthMonthReport: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [thirteenthMonthData, setThirteenthMonthData] = useState<ThirteenthMonthData[]>([]);
  const [filteredData, setFilteredData] = useState<ThirteenthMonthData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filter states
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [searchTerm, setSearchTerm] = useState('');
  const [eligibilityFilter, setEligibilityFilter] = useState<'all' | 'eligible' | 'not-eligible'>('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const reportRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchEmployees();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      calculateThirteenthMonthPay();
    }
  }, [employees, selectedYear]);

  useEffect(() => {
    applyFilters();
  }, [thirteenthMonthData, selectedDepartment, searchTerm, eligibilityFilter]);

  const fetchEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('status', 'active');
      
      if (error) throw error;
      
      // Filter out admin accounts
      const nonAdminEmployees = (data || []).filter(employee => {
        const isAdmin = employee.position.toLowerCase().includes('system administrator') || 
                       employee.position.toLowerCase().includes('admin') ||
                       employee.email?.toLowerCase().includes('admin@');
        return !isAdmin;
      });
      
      setEmployees(nonAdminEmployees);
    } catch (err) {
      setError('Failed to fetch employees');
      console.error('Error fetching employees:', err);
    }
  };

  const fetchDepartments = async () => {
    try {
      const { data, error } = await supabase
        .from('departments')
        .select('*');
      
      if (error) throw error;
      setDepartments(data || []);
    } catch (err) {
      console.error('Error fetching departments:', err);
    }
  };

  const calculateThirteenthMonthPay = async () => {
    setLoading(true);
    try {
      const year = parseInt(selectedYear);
      const yearStart = new Date(year, 0, 1);
      const yearEnd = new Date(year, 11, 31);
      
      const thirteenthMonthCalculations: ThirteenthMonthData[] = [];
      
      for (const employee of employees) {
        // Get payroll data for the selected year
        const { data: payrollData, error } = await supabase
          .from('payrolls')
          .select('basic_salary, pay_period_start, pay_period_end')
          .eq('employee_id', employee.id)
          .gte('pay_period_start', yearStart.toISOString())
          .lte('pay_period_end', yearEnd.toISOString());
        
        if (error) {
          console.error('Error fetching payroll data:', error);
          continue;
        }
        
        // Calculate total basic salary for the year
        const totalBasicSalary = payrollData?.reduce((sum, payroll) => sum + payroll.basic_salary, 0) || 0;
        
        // Calculate exact months worked based on hire date
        const hireDate = new Date(employee.hire_date);
        const effectiveStartDate = hireDate > yearStart ? hireDate : yearStart;
        
        // Calculate the end date for the calculation
        // If we're calculating for current year, use current date; otherwise use year end
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const calculationEndDate = year === currentYear ? currentDate : yearEnd;
        
        // Calculate months worked more precisely
        let monthsWorked = 0;
        
        if (effectiveStartDate <= calculationEndDate) {
          // Calculate the difference in months
          const startYear = effectiveStartDate.getFullYear();
          const startMonth = effectiveStartDate.getMonth();
          const startDay = effectiveStartDate.getDate();
          
          const endYear = calculationEndDate.getFullYear();
          const endMonth = calculationEndDate.getMonth();
          const endDay = calculationEndDate.getDate();
          
          // Calculate full months between start and end
          monthsWorked = (endYear - startYear) * 12 + (endMonth - startMonth);
          
          // Add partial month calculation
          if (startDay === 1) {
            // Started on first day of month, count full month
            monthsWorked += 1;
          } else {
            // Calculate partial first month
            const daysInStartMonth = new Date(startYear, startMonth + 1, 0).getDate();
            const daysWorkedInStartMonth = daysInStartMonth - startDay + 1;
            const partialStartMonth = daysWorkedInStartMonth / daysInStartMonth;
            monthsWorked += partialStartMonth;
          }
          
          // Handle partial end month if not at month end
          if (year === currentYear && endDay < new Date(endYear, endMonth + 1, 0).getDate()) {
            const daysInEndMonth = new Date(endYear, endMonth + 1, 0).getDate();
            const daysWorkedInEndMonth = endDay;
            const partialEndMonth = daysWorkedInEndMonth / daysInEndMonth;
            monthsWorked = monthsWorked - 1 + partialEndMonth;
          }
        }
        
        // Ensure minimum of 0 months and round to 2 decimal places
        monthsWorked = Math.max(0, Math.round(monthsWorked * 100) / 100);
        
        // Calculate service months from hire date to end of year
        const serviceMonths = Math.floor(
          (yearEnd.getTime() - hireDate.getTime()) / (1000 * 60 * 60 * 24 * 30.44)
        );
        
        // 13th month pay calculation - pro-rated based on months worked
        // Employee must have worked at least 1 month to be eligible
        const isEligible = monthsWorked >= 1;
        
        // Pro-rated 13th month pay: (Monthly Basic Salary × Months Worked) / 12
        // Or: (Total Basic Salary for the year × Months Worked) / 12
        const monthlyBasicSalary = totalBasicSalary / Math.max(monthsWorked, 1);
        const thirteenthMonthPay = isEligible ? (monthlyBasicSalary * monthsWorked) / 12 : 0;
        
        thirteenthMonthCalculations.push({
          employee,
          totalBasicSalary,
          monthsWorked: Math.round(monthsWorked * 100) / 100, // Round to 2 decimal places
          thirteenthMonthPay,
          eligibilityStatus: isEligible ? 'Eligible' : 'Not Eligible',
          serviceMonths
        });
      }
      
      setThirteenthMonthData(thirteenthMonthCalculations);
    } catch (err) {
      setError('Failed to calculate 13th month pay');
      console.error('Error calculating 13th month pay:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...thirteenthMonthData];
    
    if (selectedDepartment) {
      filtered = filtered.filter(item => item.employee.department === selectedDepartment);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        `${item.employee.first_name} ${item.employee.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.employee.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (eligibilityFilter !== 'all') {
      filtered = filtered.filter(item => 
        eligibilityFilter === 'eligible' ? item.eligibilityStatus === 'Eligible' : item.eligibilityStatus === 'Not Eligible'
      );
    }
    
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSelectedDepartment('');
    setSearchTerm('');
    setEligibilityFilter('all');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(20);
    doc.text(`13th Month Pay Report - ${selectedYear}`, 20, 20);
    
    // Add generation date
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
    
    // Prepare table data
    const tableData = filteredData.map(item => [
      item.employee.id,
      `${item.employee.first_name} ${item.employee.last_name}`,
      item.employee.department,
      item.employee.position,
      `₱${item.totalBasicSalary.toLocaleString()}`,
      item.monthsWorked.toString(),
      `₱${item.thirteenthMonthPay.toLocaleString()}`,
      item.eligibilityStatus
    ]);
    
    // Add table
    (doc as any).autoTable({
      head: [['Employee ID', 'Name', 'Department', 'Position', 'Total Basic Salary', 'Months Worked', '13th Month Pay', 'Status']],
      body: tableData,
      startY: 40,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [59, 130, 246] }
    });
    
    doc.save(`13th-month-report-${selectedYear}.pdf`);
  };

  const exportToExcel = () => {
    const worksheetData = filteredData.map(item => ({
      'Employee ID': item.employee.id,
      'Name': `${item.employee.first_name} ${item.employee.last_name}`,
      'Department': item.employee.department,
      'Position': item.employee.position,
      'Hire Date': new Date(item.employee.hire_date).toLocaleDateString(),
      'Total Basic Salary': item.totalBasicSalary,
      'Months Worked': item.monthsWorked,
      'Service Months': item.serviceMonths,
      '13th Month Pay': item.thirteenthMonthPay,
      'Eligibility Status': item.eligibilityStatus
    }));
    
    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '13th Month Report');
    
    XLSX.writeFile(workbook, `13th-month-report-${selectedYear}.xlsx`);
  };

  // Calculate summary statistics
  const totalEligibleEmployees = filteredData.filter(item => item.eligibilityStatus === 'Eligible').length;
  const totalThirteenthMonthPayout = filteredData
    .filter(item => item.eligibilityStatus === 'Eligible')
    .reduce((sum, item) => sum + item.thirteenthMonthPay, 0);
  const averageThirteenthMonthPay = totalEligibleEmployees > 0 ? totalThirteenthMonthPayout / totalEligibleEmployees : 0;

  // Pagination
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + itemsPerPage);

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>Loading 13th month report...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }} ref={reportRef}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: '#1e293b',
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              bgcolor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: 48,
              height: 48,
            }}
          >
            <CalendarTodayIcon />
          </Avatar>
          13th Month Pay Report
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748b' }}>
          Calculate and manage 13th month pay for all employees
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filter Controls */}
      <Card
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
          mb: 4,
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 'bold',
              color: '#1e293b',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <FilterListIcon sx={{ color: '#3b82f6' }} />
            Filter Options
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
              <FormControl fullWidth>
                <InputLabel>Year</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Year"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                  }}
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return (
                      <MenuItem key={year} value={year.toString()}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  value={selectedDepartment}
                  onChange={(e) => setSelectedDepartment(e.target.value)}
                  label="Department"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                  }}
                >
                  <MenuItem value="">All Departments</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.name}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '180px' }}>
              <FormControl fullWidth>
                <InputLabel>Eligibility</InputLabel>
                <Select
                  value={eligibilityFilter}
                  onChange={(e) => setEligibilityFilter(e.target.value as any)}
                  label="Eligibility"
                  sx={{
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3b82f6',
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Employees</MenuItem>
                  <MenuItem value="eligible">Eligible Only</MenuItem>
                  <MenuItem value="not-eligible">Not Eligible</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 250px', minWidth: '200px' }}>
              <TextField
                fullWidth
                label="Search Employee"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748b' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    '&:hover fieldset': {
                      borderColor: '#3b82f6',
                    },
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: '1 1 150px', minWidth: '120px' }}>
              <Button
                fullWidth
                variant="contained"
                onClick={clearFilters}
                sx={{
                  height: 56,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  boxShadow: '0 4px 15px rgba(59, 130, 246, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                    boxShadow: '0 6px 20px rgba(59, 130, 246, 0.4)',
                  },
                }}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    width: 48,
                    height: 48,
                  }}
                >
                  <PeopleIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    {totalEligibleEmployees}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Eligible Employees
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              boxShadow: '0 8px 32px rgba(240, 147, 251, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(240, 147, 251, 0.4)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    width: 48,
                    height: 48,
                  }}
                >
                  <AttachMoneyIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    ₱{totalThirteenthMonthPayout.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Total 13th Month Payout
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              boxShadow: '0 8px 32px rgba(67, 233, 123, 0.3)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(67, 233, 123, 0.4)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    width: 48,
                    height: 48,
                  }}
                >
                  <AccountBalanceWalletIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>
                    ₱{averageThirteenthMonthPay.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Average 13th Month Pay
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Export Buttons */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<DownloadIcon />}
          onClick={exportToPDF}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #b91c1c 0%, #991b1b 100%)',
            },
          }}
        >
          Export PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<ExcelIcon />}
          onClick={exportToExcel}
          sx={{
            borderRadius: 2,
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)',
            },
          }}
        >
          Export Excel
        </Button>
      </Box>

      {/* 13th Month Pay Table */}
      <Card
        sx={{
          borderRadius: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid rgba(255, 255, 255, 0.8)',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar
                sx={{
                  bgcolor: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                  width: 40,
                  height: 40,
                }}
              >
                <ExcelIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1e293b' }}>
                  13th Month Pay Report - {selectedYear}
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b' }}>
                  {filteredData.length} employees found
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>Employee</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>Position</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>Total Basic Salary</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>Months Worked</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>13th Month Pay</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f1f5f9', color: '#1e293b', borderBottom: '2px solid #e2e8f0', py: 2 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((item) => (
                  <TableRow
                    key={item.employee.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f8fafc',
                      },
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <TableCell sx={{ py: 2 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          src={item.employee.avatar_url}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: '#3b82f6',
                          }}
                        >
                          {`${item.employee.first_name[0]}${item.employee.last_name[0]}`}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#1e293b' }}>
                            {`${item.employee.first_name} ${item.employee.last_name}`}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            ID: {item.employee.id.slice(0, 8).toUpperCase()}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {item.employee.department}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {item.employee.position}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'medium', color: '#1e293b' }}>
                        ₱{item.totalBasicSalary.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ color: '#475569' }}>
                        {item.monthsWorked} months
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#059669' }}>
                        ₱{item.thirteenthMonthPay.toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 2 }}>
                      <Chip
                        label={item.eligibilityStatus}
                        size="small"
                        sx={{
                          borderRadius: 2,
                          fontWeight: 'medium',
                          ...(item.eligibilityStatus === 'Eligible'
                            ? {
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                border: '1px solid #bbf7d0',
                              }
                            : {
                                backgroundColor: '#fef2f2',
                                color: '#dc2626',
                                border: '1px solid #fecaca',
                              }),
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            justifyContent: 'center',
            p: 3,
            border: '1px solid #e2e8f0',
            borderRadius: 3,
            backgroundColor: '#ffffff',
          }}
        >
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="large"
            sx={{
              '& .MuiPaginationItem-root': {
                borderRadius: 2,
                fontWeight: 'medium',
                border: '1px solid #e2e8f0',
                '&:hover': {
                  backgroundColor: '#f1f5f9',
                  borderColor: '#3b82f6',
                },
                '&.Mui-selected': {
                  backgroundColor: '#3b82f6',
                  color: 'white',
                  borderColor: '#3b82f6',
                  '&:hover': {
                    backgroundColor: '#1d4ed8',
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default ThirteenthMonthReport;